import React, { useCallback, useEffect, useState } from "react";
import { useView } from "../../contexts/ViewContext";
import { supabase } from "../../lib/supabase";

/**
 * ActionList — what_to_do evidence component.
 *
 * The only prescriptive panel on the page. Rendered in the pinned area above tabs.
 * One row per action in array order. Title bold, detail beneath, date as chip,
 * basis small and muted. Priority 1 (official warnings) visually distinct at top.
 * Priority 4 is an opportunity — styled positively, not as an alert.
 *
 * Actions are generated in SQL, not by a model. Rendered verbatim.
 *
 * UI alignment: follows the Event Intelligence / MetricCardShell pattern —
 * rounded-xl, shadow-sm, border-t-[3px] tone accent, ring-1, subtle tone tints
 * (bg-{tone}-500/[0.06]), inline SVG icons, text-xs minimum font size.
 */

// ─── Kind → icon + colour ──────────────────────────────────────────────────────
// Inline SVG path children (rendered inside <svg viewBox="0 0 24 24">).
// Matches the EI icon style: stroke-based, 24×24, no fill.
const ICON_PATHS = {
  warning: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  fire: (
    <path d="M12 2c0 4-4 4-4 8a4 4 0 0 0 8 0c0-2-1-3-2-4 0 2-1 3-2 3 0-3 0-5 0-7z" />
  ),
  air: (
    <>
      <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m11.59-11.41A2 2 0 1 1 16 8h6m-4.41 11.41A2 2 0 1 0 20 16H2" />
    </>
  ),
  arrivals: (
    <>
      <path d="M5 17h14M5 17v-5l3-5h8l3 5v5M5 17v2h2v-2M17 17v2h2v-2" />
      <circle cx="8" cy="17" r="1.5" />
      <circle cx="16" cy="17" r="1.5" />
    </>
  ),
  opportunity: (
    <>
      <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1v.2h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" />
    </>
  ),
  sites: (
    <>
      <path d="M12 2L2 22h20L12 2z" />
      <path d="M12 2v20M8 12h8" />
    </>
  ),
  bugs: (
    <>
      <rect x="8" y="6" width="8" height="14" rx="4" />
      <path d="M8 10H4M8 14H3M8 18H4M16 10h4M16 14h5M16 18h4M12 6V3M10 4l-2-1M14 4l2-1" />
    </>
  ),
  freeze: (
    <>
      <line x1="12" y1="2" x2="12" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="5" y1="5" x2="19" y2="19" />
      <line x1="19" y1="5" x2="5" y2="19" />
    </>
  ),
  promote: (
    <>
      <path d="M3 11l18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </>
  ),
  expectations: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 7h8M8 11h8M8 15h5" />
    </>
  ),
};

// ─── Kind → tone style (matches MetricCardShell TONE_STYLES pattern) ──────────
// Subtle tints, top-border accent, ring — the EI card look.
const KIND_STYLE = {
  warning: {
    icon: "warning",
    bg: "bg-red-500/[0.06] dark:bg-red-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-red-500",
    ring: "ring-1 ring-red-500/20 dark:ring-red-500/20",
    badge: "bg-red-500/15 dark:bg-red-500/20 text-red-600 dark:text-red-400",
    iconColor: "text-red-500",
  },
  fire: {
    icon: "fire",
    bg: "bg-orange-500/[0.06] dark:bg-orange-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-orange-500",
    ring: "ring-1 ring-orange-500/20 dark:ring-orange-500/20",
    badge: "bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400",
    iconColor: "text-orange-500",
  },
  air: {
    icon: "air",
    bg: "bg-purple-500/[0.06] dark:bg-purple-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-purple-500",
    ring: "ring-1 ring-purple-500/20 dark:ring-purple-500/20",
    badge: "bg-purple-500/15 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400",
    iconColor: "text-purple-500",
  },
  arrivals: {
    icon: "arrivals",
    bg: "bg-blue-500/[0.06] dark:bg-blue-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-blue-500",
    ring: "ring-1 ring-blue-500/20 dark:ring-blue-500/20",
    badge: "bg-blue-500/15 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400",
    iconColor: "text-blue-500",
  },
  opportunity: {
    icon: "opportunity",
    bg: "bg-emerald-500/[0.06] dark:bg-emerald-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-emerald-500",
    ring: "ring-1 ring-emerald-500/20 dark:ring-emerald-500/20",
    badge: "bg-emerald-500/15 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400",
    iconColor: "text-emerald-500",
  },
  sites: {
    icon: "sites",
    bg: "bg-amber-500/[0.06] dark:bg-amber-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-amber-500",
    ring: "ring-1 ring-amber-500/20 dark:ring-amber-500/20",
    badge: "bg-amber-500/15 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400",
    iconColor: "text-amber-500",
  },
  bugs: {
    icon: "bugs",
    bg: "bg-yellow-500/[0.06] dark:bg-yellow-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-yellow-500",
    ring: "ring-1 ring-yellow-500/20 dark:ring-yellow-500/20",
    badge: "bg-yellow-500/15 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400",
    iconColor: "text-yellow-500",
  },
  freeze: {
    icon: "freeze",
    bg: "bg-cyan-500/[0.06] dark:bg-cyan-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-cyan-500",
    ring: "ring-1 ring-cyan-500/20 dark:ring-cyan-500/20",
    badge: "bg-cyan-500/15 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400",
    iconColor: "text-cyan-500",
  },
  promote: {
    icon: "promote",
    bg: "bg-pink-500/[0.06] dark:bg-pink-500/[0.08]",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-pink-500",
    ring: "ring-1 ring-pink-500/20 dark:ring-pink-500/20",
    badge: "bg-pink-500/15 dark:bg-pink-500/20 text-pink-600 dark:text-pink-400",
    iconColor: "text-pink-500",
  },
  expectations: {
    // Spec §0d: #94A3B8 (slate-400), "muted, no fill" — plain bg, not tinted
    icon: "expectations",
    bg: "bg-white dark:bg-gray-900",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-slate-400 dark:border-t-slate-600",
    ring: "ring-1 ring-slate-400/20 dark:ring-slate-600/20",
    badge: "bg-slate-400/15 dark:bg-slate-600/20 text-slate-500 dark:text-slate-400",
    iconColor: "text-slate-400 dark:text-slate-500",
  },
};

const getDefaultKindStyle = (kind) => KIND_STYLE[kind] ?? KIND_STYLE.expectations;

// ─── Date formatter (matches CI AiPricingActionsPanel pattern) ────────────────
// "2026-08-11" → "Aug 11, 2026". Parsed as a plain date string to avoid UTC shift.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtActionDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${MONTHS[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
};

// ─── Priority label ─────────────────────────────────────────────────────────────
// Priority 1 = official warning, 2 = act this week, 3 = worth doing, 4 = opportunity
const PRIORITY_LABEL = {
  1: "Warning",
  2: "Act now",
  3: "Worth doing",
  4: "Opportunity",
};

const PRIORITY_STYLE = {
  1: "bg-red-500/15 dark:bg-red-500/20 text-red-600 dark:text-red-400 ring-1 ring-red-500/30",
  2: "bg-orange-500/15 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400",
  3: "bg-yellow-500/15 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400",
  4: "bg-emerald-500/15 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400",
};

// ─── Historical-snapshot notice (collapsed what_to_do) ───────────────────────
// When the owner is viewing a non-today snapshot, the whole action list collapses
// behind this note. Live guidance only makes sense for today; a past snapshot's
// "what to do this week" is reference, not a to-do. Expand reveals the rows.
const HistoricalNote = ({ expanded, onToggle }) => (
  <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/[0.08] p-3">
    <svg
      className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-amber-800 dark:text-amber-300 leading-snug">
        These recommendations are from a past snapshot and may no longer be relevant to current conditions.
      </p>
      <button
        type="button"
        onClick={onToggle}
        className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 transition-colors"
      >
        {expanded ? "Collapse" : "Expand to review"}
      </button>
    </div>
  </div>
);

const ActionList = ({ panel, isHistorical = false }) => {
  // clientId comes from ViewContext (the page passes only `panel` to evidence
  // components). Used to namespace each property's dismissed-action set.
  const view = useView();
  const clientId = view?.supabaseClientId ?? null;

  const f = panel.facts || {};
  const actions = f.actions || [];

  // Toggle whether dismissed rows are rendered (faded) or hidden.
  const [showDismissed, setShowDismissed] = useState(false);

  // Historical snapshot (as-of date ≠ today): the whole list collapses behind a
  // "past recommendations" note. Expand reveals the rows for reference.
  const [historicalExpanded, setHistoricalExpanded] = useState(false);

  // Dismissed actions from the server (fn_get_weather_action_dismissals).
  // Each entry is { date, kind } — matched on date + kind only, not title.
  const [dismissed, setDismissed] = useState([]);
  const [dismissedLoading, setDismissedLoading] = useState(false);

  // Reusable fetch of dismissed actions for the current client. Read RPC returns
  // { data, error }; on any Postgres-level error (or non-array data), fail safe to
  // [] — Postgres errors are never surfaced to users.
  const loadDismissed = useCallback(async () => {
    if (!clientId) {
      setDismissed([]);
      return;
    }
    setDismissedLoading(true);
    try {
      const { data, error } = await supabase.rpc("fn_get_weather_action_dismissals", {
        p_client_id: clientId,
      });
      if (error || !Array.isArray(data)) {
        setDismissed([]);
      } else {
        setDismissed(data.map((d) => ({ date: d.date, kind: d.kind })));
      }
    } catch {
      setDismissed([]);
    } finally {
      setDismissedLoading(false);
    }
  }, [clientId]);

  // Fetch on mount and whenever the selected property changes.
  useEffect(() => {
    loadDismissed();
  }, [loadDismissed]);

  // Dismiss an action. Optimistically adds { date, kind } to the dismissed array
  // before the write RPC resolves, then re-fetches to confirm the true server state
  // (this reverts the optimistic add if the call failed). Write RPCs return
  // { success, error, message } inside data; the re-fetch reconciles any failure.
  const handleDismiss = async (action) => {
    const key = { date: action.date, kind: action.kind };
    setDismissed((prev) =>
      prev.some((d) => d.date === key.date && d.kind === key.kind)
        ? prev
        : [...prev, key]
    );
    try {
      await supabase.rpc("fn_weather_dismiss_action", {
        p_client_id: clientId,
        p_action_date: action.date,
        p_action_kind: action.kind,
        p_action_title: action.title,
      });
    } finally {
      await loadDismissed();
    }
  };

  // Restore a dismissed action. Optimistically removes { date, kind } before the
  // write RPC resolves, then re-fetches to confirm (reverts the removal on failure).
  const handleRestore = async (action) => {
    const key = { date: action.date, kind: action.kind };
    setDismissed((prev) =>
      prev.filter((d) => !(d.date === key.date && d.kind === key.kind))
    );
    try {
      await supabase.rpc("fn_weather_restore_action", {
        p_client_id: clientId,
        p_action_date: action.date,
        p_action_kind: action.kind,
      });
    } finally {
      await loadDismissed();
    }
  };

  // One stable row per action carrying its dismissed flag (matched on date + kind).
  const rows = actions.map((action, i) => ({
    action,
    i,
    dismissed: dismissed.some(
      (d) => d.date === action.date && d.kind === action.kind
    ),
  }));
  const dismissedCount = rows.filter((r) => r.dismissed).length;
  const visibleRows = showDismissed ? rows : rows.filter((r) => !r.dismissed);

  if (actions.length === 0) return null;

  // Historical snapshot: collapse the whole list behind a past-snapshot note.
  // The owner can expand to review; this is reference, not live guidance.
  if (isHistorical && !historicalExpanded) {
    return (
      <div className="space-y-3">
        <HistoricalNote expanded={false} onToggle={() => setHistoricalExpanded(true)} />
      </div>
    );
  }

  if (visibleRows.length === 0) {
    return (
      <div className="space-y-3">
        {isHistorical && (
          <HistoricalNote expanded onToggle={() => setHistoricalExpanded(false)} />
        )}
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          All actions reviewed.
        </p>
        {dismissedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowDismissed(true)}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            {dismissedCount} dismissed · show
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {isHistorical && (
        <HistoricalNote expanded onToggle={() => setHistoricalExpanded(false)} />
      )}
      {visibleRows.map(({ action, i, dismissed }) => {
        const style = getDefaultKindStyle(action.kind);
        const priorityStyle = PRIORITY_STYLE[action.priority] || PRIORITY_STYLE[3];
        const priorityLabel = PRIORITY_LABEL[action.priority] || `Priority ${action.priority}`;

        return (
          <div
            key={i}
            className={`relative ${style.bg} rounded-xl shadow-sm border border-t-[3px] ${style.borderTop} ${style.border} ${style.ring} p-4 transition-shadow ${dismissed ? "opacity-50" : ""}`}
          >
            {/* Dismiss / restore button — persists per client via the dismissals RPC */}
            {dismissed ? (
              <button
                type="button"
                onClick={() => handleRestore(action)}
                aria-label="Restore action"
                className="absolute top-3 right-3 p-1 text-gray-400 hover:text-emerald-600 dark:text-gray-500 dark:hover:text-emerald-400 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleDismiss(action)}
                aria-label="Dismiss action"
                className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
            <div className="flex items-start gap-3">
              {/* Icon */}
              <svg
                className={`w-5 h-5 ${style.iconColor} flex-shrink-0 mt-0.5`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-label={action.kind}
                role="img"
              >
                {ICON_PATHS[style.icon] ?? ICON_PATHS.expectations}
              </svg>

              <div className="flex-1 min-w-0">
                {/* Title + priority badge row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-snug">
                    {action.title}
                  </p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${priorityStyle}`}>
                    {priorityLabel}
                  </span>
                </div>

                {/* Meta line — basis + date, CI style: plain text with dot separators */}
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
                  {action.basis && (
                    <span>Based on {action.basis}</span>
                  )}
                  {action.basis && action.date && (
                    <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">·</span>
                  )}
                  {action.date && (
                    <span className="whitespace-nowrap">{fmtActionDate(action.date)}</span>
                  )}
                </div>

                {/* Detail */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">
                  {action.detail}
                </p>
              </div>
            </div>
          </div>
        );
      })}

      {/* Show / hide dismissed actions */}
      {dismissedCount > 0 && (
        <button
          type="button"
          onClick={() => setShowDismissed((s) => !s)}
          className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          {showDismissed ? "Hide dismissed" : `${dismissedCount} dismissed · show`}
        </button>
      )}
    </div>
  );
};

export default ActionList;
